import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Typography, Tabs, TabsList, TabsTrigger } from "@altimateai/lego";
import WorkflowTasks from "./WorkflowTasks";
import WorkflowDashboard from "./WorkflowDashboard";
import WorkflowAgentConfig from "./WorkflowAgentConfig";
import WorkflowSessions from "./WorkflowSessions";
import WorkflowSessionDetail from "./WorkflowSessionDetail";
import WorkflowTools from "./WorkflowTools";
import WorkflowAutomations from "./WorkflowAutomations";
import styles from "./workflow.module.scss";

const TABS = [
  { key: "overview", label: "Overview", path: "overview" },
  { key: "sessions", label: "Sessions", path: "sessions" },
  { key: "agents", label: "Agent Fleet", path: "agents" },
  { key: "automations", label: "Automations", path: "automations" },
  { key: "tools", label: "Tools", path: "tools" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const Workflow = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Derive active tab from URL
  const pathAfterWorkflow = location.pathname.replace(/^\/workflow\/?/, "");
  const activeTab: TabKey = TABS.some(t => pathAfterWorkflow.startsWith(t.path))
    ? (TABS.find(t => pathAfterWorkflow.startsWith(t.path))!.key as TabKey)
    : "overview";

  return (
    <div className={styles.workflow}>
      {/* Header and tabs always visible */}
      <div className={styles.pageHeader}>
        <Typography variant="page-heading">Workflow</Typography>
      </div>

      <Tabs value={activeTab} onValueChange={val => navigate(`/workflow/${val}`)}>
        <TabsList>
          {TABS.map(tab => (
            <TabsTrigger key={tab.key} value={tab.key}>{tab.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Content area */}
      <Routes>
        <Route path="overview" element={<WorkflowTasks />} />
        <Route
          path="agents"
          element={
            <WorkflowDashboard
              onViewConfig={agentId => navigate(`/workflow/agents/${agentId}`)}
              lastRefreshed={new Date()}
            />
          }
        />
        <Route
          path="agents/:agentId"
          element={
            <WorkflowAgentConfig
              agentId=""
              onBack={() => navigate("/workflow/agents")}
              lastRefreshed={new Date()}
            />
          }
        />
        <Route
          path="sessions"
          element={
            <WorkflowSessions
              onViewSession={sessionId => navigate(`/workflow/sessions/${sessionId}`)}
              lastRefreshed={new Date()}
            />
          }
        />
        <Route path="sessions/:sessionId" element={<WorkflowSessionDetail />} />
        <Route path="automations" element={<WorkflowAutomations />} />
        <Route path="tools" element={<WorkflowTools />} />
        {/* Default: land on Tasks */}
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="*" element={<Navigate to="overview" replace />} />
      </Routes>
    </div>
  );
};

export default Workflow;
